import { useEffect, useState, useMemo } from "react";
import { api, asset } from "../lib/api";
import { WeddingMosaic } from "../components/WeddingMosaic";
import { Eye, Camera, Images, Share2, Heart, Check, Loader2 } from "lucide-react";
import { useText } from "../lib/siteText";
import { VendorStatement, MomentStories, SocialClips } from "../components/IncognitoSections";
import { LegacySection } from "../components/LegacySection";

const RECEIVE_ICONS = [Camera, Images, Share2];

export default function IncognitoPaparazzi() {
  const [weddingImgs, setWeddingImgs] = useState([]);
  const [stories, setStories] = useState([]);
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({ name: "", wedding_date: "", venue: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const t = useText();
  const STEPS = [1, 2, 3, 4].map((n) => ({ t: t(`inc_step${n}_t`), d: t(`inc_step${n}_d`) }));
  const RECEIVE = [1, 2, 3].map((n, i) => ({ icon: RECEIVE_ICONS[i], t: t(`inc_recv${n}_t`), d: t(`inc_recv${n}_d`) }));

  useEffect(() => {
    document.title = "Incognito Paparazzi — Scott Burton Photography";
    api.get("/incognito/photos")
      .then(({ data }) => setWeddingImgs([...data].sort(() => Math.random() - 0.5).slice(0, 64)));
    api.get("/incognito/stories").then(({ data }) => setStories(data)).catch(() => {});
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("inc_ref", ref.slice(0, 40));
    api.get("/incognito/assets").then(({ data }) => setAssets(data)).catch(() => {});
  }, []);

  const heroImgs = useMemo(() => weddingImgs, [weddingImgs]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/incognito/request", { ...form, ref: localStorage.getItem("inc_ref") || null });
      setDone(true);
    } catch {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="incognito-page">
      {/* HERO */}
      <section className="relative h-[85vh] min-h-[520px] w-full overflow-hidden">
        <WeddingMosaic images={heroImgs.length ? heroImgs : []} tiles={64} overlay={0.78}>
          <div className="text-center max-w-3xl animate-fade-up">
            <div className="overline-label mb-5 flex items-center justify-center gap-2 text-amber-glow">
              <Eye size={14} /> A new kind of wedding coverage
            </div>
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl font-light leading-[0.9]">
              Incognito<br />Paparazzi
            </h1>
            <p className="mt-6 font-serif text-xl lg:text-2xl font-light text-neutral-200 italic" data-testid="incognito-subhead">{t("inc_subhead")}</p>
            <a href="#request" data-testid="hero-request-cta" className="inline-flex mt-9 btn-amber rounded-full px-7 py-3 items-center gap-2">
              Request Incognito Coverage
            </a>
          </div>
        </WeddingMosaic>
      </section>

      <LegacySection limit={8} testid="incognito-legacy" />

      {/* THE CONCEPT */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="overline-label mb-5">The concept</div>
        <p className="font-serif text-2xl lg:text-4xl font-light leading-snug">{t("inc_concept")}</p>
      </section>

      <VendorStatement text={t("inc_vendor")} />
      <MomentStories pairs={stories} intro={t("inc_stories_intro")} />
      <SocialClips clips={assets.filter((a) => a.kind === "clip")} posters={weddingImgs} intro={t("inc_clips_intro")} />

      {/* HOW IT WORKS */}
      <section className="border-y border-white/8 bg-obsidian-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="overline-label mb-3">How it works</div>
          <h2 className="font-serif text-4xl lg:text-5xl font-light mb-12">Candid, by design</h2>
          <div className="grid gap-px sm:grid-cols-2 lg:grid-cols-4 bg-white/8 rounded-lg overflow-hidden">
            {STEPS.map((s, i) => (
              <div key={i} data-testid={`incognito-step-${i}`} className="bg-obsidian-800 p-8">
                <div className="font-mono text-amber-glow text-sm mb-4">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-serif text-xl font-light mb-2">{s.t}</div>
                <p className="text-neutral-400 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU RECEIVE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="overline-label mb-3">What you receive</div>
        <h2 className="font-serif text-4xl lg:text-5xl font-light mb-12">More than a gallery</h2>
        <div className="grid gap-8 md:grid-cols-3">
          {RECEIVE.map((r, i) => {
            const Icon = r.icon;
            return (
              <div key={i} data-testid={`incognito-receive-${i}`} className="border border-white/10 rounded-lg p-8 bg-obsidian-800 hover:border-amber-glow/40 transition-colors">
                <Icon className="text-amber-glow mb-5" size={26} />
                <div className="font-serif text-2xl font-light mb-2">{r.t}</div>
                <p className="text-neutral-400 text-sm leading-relaxed">{r.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHY I'M DOING THIS */}
      <section className="border-y border-white/8">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
          <Heart className="text-amber-glow mx-auto mb-6" size={28} />
          <div className="overline-label mb-4">Why I'm doing this</div>
          <p className="font-serif text-2xl lg:text-3xl font-light leading-snug">{t("inc_why")}</p>
        </div>
      </section>

      {/* CTA FORM */}
      <section id="request" className="max-w-2xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center mb-10">
          <div className="overline-label mb-3">Request coverage</div>
          <h2 className="font-serif text-4xl lg:text-5xl font-light">Request Incognito Coverage</h2>
          <p className="mt-4 text-neutral-400">{t("inc_form_note")}</p>
        </div>

        {done ? (
          <div data-testid="incognito-success" className="border border-amber-glow/40 rounded-lg p-10 text-center bg-obsidian-800">
            <Check className="text-amber-glow mx-auto mb-4" size={34} />
            <div className="font-serif text-2xl font-light mb-2">Request received</div>
            <p className="text-neutral-400">{t("inc_success")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="incognito-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="incognito-name" required placeholder="Your name" value={form.name} onChange={set("name")}
                className="bg-obsidian-800 border border-white/12 rounded-lg px-4 py-3 text-sm focus:border-amber-glow/60 outline-none" />
              <input data-testid="incognito-date" type="date" placeholder="Wedding date" value={form.wedding_date} onChange={set("wedding_date")}
                className="bg-obsidian-800 border border-white/12 rounded-lg px-4 py-3 text-sm text-neutral-300 focus:border-amber-glow/60 outline-none" />
            </div>
            <input data-testid="incognito-venue" placeholder="Venue / city" value={form.venue} onChange={set("venue")}
              className="w-full bg-obsidian-800 border border-white/12 rounded-lg px-4 py-3 text-sm focus:border-amber-glow/60 outline-none" />
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="incognito-email" type="email" required placeholder="Email" value={form.email} onChange={set("email")}
                className="bg-obsidian-800 border border-white/12 rounded-lg px-4 py-3 text-sm focus:border-amber-glow/60 outline-none" />
              <input data-testid="incognito-phone" placeholder="Phone" value={form.phone} onChange={set("phone")}
                className="bg-obsidian-800 border border-white/12 rounded-lg px-4 py-3 text-sm focus:border-amber-glow/60 outline-none" />
            </div>
            <textarea data-testid="incognito-message" rows={3} placeholder="Anything I should know? (optional)" value={form.message} onChange={set("message")}
              className="w-full bg-obsidian-800 border border-white/12 rounded-lg px-4 py-3 text-sm focus:border-amber-glow/60 outline-none" />
            <button data-testid="incognito-submit" type="submit" disabled={sending}
              className="w-full btn-amber rounded-full px-6 py-3.5 flex items-center justify-center gap-2 disabled:opacity-60">
              {sending ? <Loader2 size={16} className="animate-spin" /> : null} Request Incognito Coverage
            </button>
          </form>
        )}

        <p className="mt-8 text-center text-sm text-neutral-500">
          Prefer the traditional route? Full wedding packages start at <span className="text-neutral-300">{t("inc_traditional_price")}</span>. <a href="/contact" className="text-amber-glow link-underline">Get in touch</a>.
          <br /><a href="/planners" data-testid="incognito-planners-link" className="inline-block mt-2 text-neutral-400 hover:text-amber-glow link-underline">Wedding planner or venue? Become a referral partner</a>
        </p>
      </section>
    </div>
  );
}
